import { Link } from "wouter";
import { useEffect, useState, createContext, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AuthDialog from "@/components/auth-dialog";

type AuthOpener = (mode: "signin" | "signup") => void;
const AuthCtx = createContext<AuthOpener>(() => {});
const useAuthDialog = () => useContext(AuthCtx);
import {
  Bot, Sparkles, MessageSquare, Phone, Ticket, Headphones,
  Wrench, Workflow, ShieldCheck, KeyRound, Plug, Globe,
  BarChart3, Database, Layers, FileCheck, Building2, GraduationCap,
  Briefcase, LifeBuoy, ArrowRight, Check, Zap, Lock, Code2,
  Smartphone, Mail, Slack, Webhook, Palette, Users,
  Eye, ChevronRight, Star,
} from "lucide-react";

/**
 * Public marketing landing page — light mode only.
 * Showcases the full agentic-bot platform: AI tools, channels, live chat,
 * voice, tickets, SSO, knowledge, branding, audit, analytics.
 */
export default function MarketingLanding() {
  // Force light theme while this page is visible — the platform's
  // ThemeProvider may have stored "dark" from a prior session; the
  // landing page is light-only by design.
  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => { if (had) root.classList.add("dark"); };
  }, []);

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const openAuth: AuthOpener = (mode) => { setAuthMode(mode); setAuthOpen(true); };

  return (
    <AuthCtx.Provider value={openAuth}>
    <div className="min-h-screen bg-gradient-to-b from-sky-200 via-cyan-100 to-blue-200 text-slate-900 antialiased">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:rounded-md focus:bg-indigo-600 focus:text-white">
        Skip to main content
      </a>
      <Nav />
      <main id="main">
        <Hero />
        <LogoStrip />
        <Capabilities />
        <AgentLoop />
        <Channels />
        <ToolsAndActions />
        <LiveOps />
        <KnowledgeAndAI />
        <Enterprise />
        <Analytics />
        <UseCases />
        <FinalCta />
      </main>
      <Footer />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} mode={authMode} />
    </div>
    </AuthCtx.Provider>
  );
}

/* ============================================================
   NAV
   ============================================================ */
function Nav() {
  const openAuth = useAuthDialog();
  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-sky-200/80 border-b border-sky-300/60">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2" data-testid="link-home">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-white shadow-sm">
            <Bot className="w-5 h-5" />
          </div>
          <span className="font-semibold tracking-tight text-lg">CampusAssist</span>
          <Badge variant="secondary" className="ml-1 hidden sm:inline-flex bg-indigo-50 text-indigo-700 border-indigo-100">
            Agentic AI
          </Badge>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
          <a href="#capabilities" className="hover:text-slate-900">Capabilities</a>
          <a href="#channels"     className="hover:text-slate-900">Channels</a>
          <a href="#enterprise"   className="hover:text-slate-900">Enterprise</a>
          <a href="#usecases"     className="hover:text-slate-900">Use cases</a>
        </nav>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => openAuth("signin")} data-testid="button-nav-login">Sign in</Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => openAuth("signup")} data-testid="button-nav-signup">
            Get started
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
   HERO
   ============================================================ */
function Hero() {
  const openAuth = useAuthDialog();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.10),_transparent_60%)]" />
      <div className="absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(circle_at_70%_20%,_rgba(34,211,238,0.18),_transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Badge className="bg-white border border-indigo-200 text-indigo-700 hover:bg-white">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Now with agentic tools, voice & live-chat handoff
          </Badge>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            The agentic AI <span className="bg-gradient-to-r from-indigo-600 to-cyan-500 bg-clip-text text-transparent">workforce</span> for your organisation
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            CampusAssist deploys grounded, tool-using AI agents across web, mobile, voice and chat —
            with enterprise SSO, audit and human handoff built in. One platform, every channel,
            full observability.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => openAuth("signup")} data-testid="button-hero-cta">
              Get started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <a href="#capabilities">
              <Button size="lg" variant="outline" className="border-slate-300" data-testid="button-hero-tour">
                Take the tour
              </Button>
            </a>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> SOC-2-friendly architecture</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Enterprise SSO &amp; SCIM</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <HeroChatPreview />
        </div>
      </div>
    </section>
  );
}

function HeroChatPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-indigo-200/50 via-cyan-200/40 to-transparent rounded-3xl blur-2xl -z-10" />
      <div className="rounded-2xl border border-sky-300/70 bg-white/90 backdrop-blur-sm shadow-2xl shadow-sky-900/15 overflow-hidden">
        <div className="px-4 h-11 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
          <span className="ml-2 text-xs text-slate-500">CampusAssist · IT Support Bot</span>
          <Badge className="ml-auto bg-emerald-50 text-emerald-700 border-emerald-100 text-[10px]">Live</Badge>
        </div>
        <div className="p-5 space-y-4 bg-white">
          <Msg side="user">My VPN keeps disconnecting. Can you fix it?</Msg>
          <Msg side="bot">
            I checked your account — your VPN cert expired 2 days ago. I can issue a new one and email install steps. Proceed?
            <ToolCallChip name="okta.issue_cert" />
          </Msg>
          <Msg side="user">Yes please.</Msg>
          <Msg side="bot">
            Done ✅ New cert issued, install guide sent to <strong>asha@acme.com</strong>. I also opened ticket <strong>#4821</strong> so IT has the audit trail.
            <ToolCallChip name="zendesk.create_ticket" />
            <ToolCallChip name="mailgun.send" />
          </Msg>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 bg-slate-50">
          <div className="flex-1 px-3 py-2 rounded-lg bg-white border border-sky-200/70 text-xs text-slate-400">Ask anything…</div>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">Send</Button>
        </div>
      </div>
    </div>
  );
}

function Msg({ side, children }: { side: "user" | "bot"; children: React.ReactNode }) {
  const isUser = side === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] text-sm rounded-2xl px-4 py-2.5 ${
        isUser
          ? "bg-indigo-600 text-white rounded-br-md"
          : "bg-slate-100 text-slate-800 rounded-bl-md"
      }`}>
        {children}
      </div>
    </div>
  );
}

function ToolCallChip({ name }: { name: string }) {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-indigo-100 bg-indigo-50/70 px-2 py-1 text-[11px] text-indigo-700 font-mono">
      <Wrench className="w-3 h-3" /> {name}
    </div>
  );
}

/* ============================================================
   LOGO STRIP
   ============================================================ */
function LogoStrip() {
  const items = ["Acme University", "Northwind Health", "Globex IT", "Initech HR", "Umbrella Support", "Stark Industries"];
  return (
    <section className="border-y border-sky-300/60 bg-sky-200/60">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-5">
          Trusted by teams at
        </p>
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 text-slate-400 font-medium">
          {items.map(i => <span key={i} className="text-sm">{i}</span>)}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CAPABILITIES — high-level feature grid
   ============================================================ */
function Capabilities() {
  const cards = [
    { icon: Bot,         color: "indigo", title: "Multi-bot builder",      body: "Spin up unlimited bots per tenant — each with its own persona, knowledge, tools and channels." },
    { icon: Wrench,      color: "cyan",   title: "Agentic tools & actions", body: "Bots don't just answer — they act. Issue certs, open tickets, push refunds, with approval workflows." },
    { icon: Database,    color: "emerald",title: "Grounded knowledge",     body: "Vector RAG across PDFs, URLs, DOCX, Confluence. Auto-cited answers, freshness controls." },
    { icon: MessageSquare,color:"violet", title: "Omni-channel chat",      body: "Web widget, fullscreen, Slack, Teams, SMS, email, REST — one brain, every surface." },
    { icon: Phone,       color: "rose",   title: "Voice & call centre",    body: "Twilio voice, queues, voicemail, callbacks, supervisor live-listen, recordings + transcripts." },
    { icon: Headphones,  color: "amber",  title: "Live-chat handoff",      body: "FIFO routing to human operators with full transcript pre-loaded. Resolve → audit." },
    { icon: Ticket,      color: "sky",    title: "Tickets & workflows",    body: "First-class tickets, or sync with Zendesk / Jira / ServiceNow via connectors." },
    { icon: ShieldCheck, color: "indigo", title: "Enterprise SSO + SCIM",  body: "SAML 2.0, OIDC, SCIM 2.0 provisioning, per-org enforcement, audit-ready." },
    { icon: Palette,     color: "violet", title: "White-label branding",   body: "Per-tenant logo, colours, custom domain — your customer never sees us." },
    { icon: BarChart3,   color: "cyan",   title: "Analytics dashboard",    body: "Volume, resolution rate, handle time, AI cost, tool-success — sliced by bot & channel." },
    { icon: Eye,         color: "rose",   title: "Audit everything",       body: "Every entity insert/update/delete recorded with user, JSON diff, and tamper-evident chain." },
  ];
  return (
    <section id="capabilities" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead
          eyebrow="Capabilities"
          title="Everything you need to ship a production AI agent"
          sub="From the first prompt to the 1,000,000th conversation — every layer of the agent stack, in one platform."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map(c => <FeatureCard key={c.title} {...c} />)}
        </div>
      </div>
    </section>
  );
}

const COLOR_MAP: Record<string, string> = {
  indigo:  "bg-indigo-100  text-indigo-700  ring-indigo-200",
  cyan:    "bg-cyan-100    text-cyan-700    ring-cyan-200",
  emerald: "bg-emerald-100 text-emerald-700 ring-emerald-200",
  violet:  "bg-violet-100  text-violet-700  ring-violet-200",
  rose:    "bg-rose-100    text-rose-700    ring-rose-200",
  amber:   "bg-amber-100   text-amber-800   ring-amber-200",
  sky:     "bg-sky-100     text-sky-700     ring-sky-200",
};

// Per-card tinted surface that picks up the icon hue — gives the
// grid a playful, candy-coloured rhythm against the sky-blue page bg.
const CARD_TINT: Record<string, string> = {
  indigo:  "bg-indigo-50/85  border-indigo-200/70  hover:border-indigo-300",
  cyan:    "bg-cyan-50/85    border-cyan-200/70    hover:border-cyan-300",
  emerald: "bg-emerald-50/85 border-emerald-200/70 hover:border-emerald-300",
  violet:  "bg-violet-50/85  border-violet-200/70  hover:border-violet-300",
  rose:    "bg-rose-50/85    border-rose-200/70    hover:border-rose-300",
  amber:   "bg-amber-50/85   border-amber-200/70   hover:border-amber-300",
  sky:     "bg-sky-50/85     border-sky-200/70     hover:border-sky-300",
};

function FeatureCard({ icon: Icon, color, title, body }: any) {
  return (
    <div className={`group rounded-xl border backdrop-blur-sm p-6 hover:shadow-lg hover:shadow-slate-900/10 hover:-translate-y-0.5 transition ${CARD_TINT[color]}`}>
      <div className={`w-10 h-10 rounded-lg ring-1 grid place-items-center ${COLOR_MAP[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}

/* ============================================================
   AGENT LOOP — “how an agentic answer is built”
   ============================================================ */
function AgentLoop() {
  const steps = [
    { icon: MessageSquare, title: "1 · Understand",     body: "Intent classified, entities extracted, conversation memory + user context loaded." },
    { icon: Database,      title: "2 · Retrieve",       body: "Hybrid vector + keyword search across your grounded knowledge. Cited sources only." },
    { icon: Wrench,        title: "3 · Plan & call tools", body: "Bot picks the right action(s), validates parameters, runs them — with approvals when needed." },
    { icon: Sparkles,      title: "4 · Respond",        body: "LLM composes a grounded, on-brand reply. Streams tokens. Logs every step for audit." },
  ];
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-sky-200/70 to-cyan-100/50">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead
          eyebrow="The agentic loop"
          title="Not a chatbot. An autonomous worker."
          sub="Every answer flows through the same four-step loop — designed for accuracy, control and auditability."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.title} className="relative rounded-xl border border-sky-200/70 bg-white/75 backdrop-blur-sm p-6 shadow-sm shadow-sky-900/5 hover:shadow-md hover:shadow-sky-900/10 transition">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 text-white grid place-items-center shadow-sm">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.body}</p>
              {i < steps.length - 1 && (
                <ChevronRight className="hidden lg:block absolute top-1/2 -right-3 w-5 h-5 text-slate-300" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CHANNELS
   ============================================================ */
function Channels() {
  const channels = [
    { icon: Globe,        label: "Web widget" },
    { icon: Smartphone,   label: "Mobile SDK" },
    { icon: MessageSquare,label: "Fullscreen chat" },
    { icon: Slack,        label: "Slack" },
    { icon: Mail,         label: "Email" },
    { icon: Phone,        label: "Voice (Twilio)" },
    { icon: Webhook,      label: "Webhooks" },
    { icon: Code2,        label: "REST API" },
  ];
  return (
    <section id="channels" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionHead
            align="left"
            eyebrow="Channels"
            title="One agent. Every surface your users live on."
            sub="Embed in 30 seconds with a single script tag, or call our REST/WebSocket API directly from any stack."
          />
          <div className="mt-6 space-y-3 text-sm text-slate-600">
            <Bullet>Stateful, per-user conversation memory across all channels.</Bullet>
            <Bullet>Per-channel persona overrides (terse on SMS, verbose on web).</Bullet>
            <Bullet>Native escalation paths: chat → live agent → call → ticket.</Bullet>
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {channels.map(c => (
            <div key={c.label} className="rounded-xl border border-sky-200/70 bg-white p-5 flex flex-col items-center gap-2 hover:border-indigo-200 hover:bg-indigo-50/40 transition">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
                <c.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium text-slate-700">{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   TOOLS & ACTIONS (with approval workflow callout)
   ============================================================ */
function ToolsAndActions() {
  return (
    <section className="py-20 lg:py-28 bg-slate-900 text-slate-100 relative overflow-hidden">
      <div className="absolute inset-0 -z-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.35),_transparent_50%),radial-gradient(circle_at_80%_60%,_rgba(34,211,238,0.25),_transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/10">
            <Wrench className="w-3.5 h-3.5 mr-1.5" /> Agentic tools
          </Badge>
          <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
            Bots that <em className="not-italic bg-gradient-to-r from-cyan-300 to-indigo-300 bg-clip-text text-transparent">do the work</em> — safely.
          </h2>
          <p className="mt-4 text-slate-300 max-w-md">
            Every action your bot can take is a versioned, typed tool — defined in your dashboard, called by the LLM,
            executed by our runtime, and audited end-to-end. Risky tools require human approval before they run.
          </p>
          <ul className="mt-8 space-y-4 text-sm">
            <DarkBullet icon={Workflow}>Approval workflows on any tool — Slack/email queue for org admins.</DarkBullet>
            <DarkBullet icon={KeyRound}>BYO API keys via AI Providers vault (encrypted at rest).</DarkBullet>
            <DarkBullet icon={ShieldCheck}>Per-role tool ACLs — Members can't trigger refunds; OrgAdmins can.</DarkBullet>
            <DarkBullet icon={FileCheck}>Every invocation logged: who, when, args, response, latency.</DarkBullet>
          </ul>
        </div>
        <div className="lg:col-span-7">
          <ToolMockup />
        </div>
      </div>
    </section>
  );
}

function DarkBullet({ icon: Icon, children }: any) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 w-7 h-7 rounded-md bg-white/10 grid place-items-center text-cyan-300">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-slate-200">{children}</span>
    </li>
  );
}

function ToolMockup() {
  const tools = [
    { name: "zendesk.create_ticket", risk: "low",      runs: "1,284" },
    { name: "okta.issue_cert",       risk: "medium",   runs: "342"   },
    { name: "stripe.refund_charge",  risk: "high",     runs: "47"    },
    { name: "workday.update_pto",    risk: "medium",   runs: "918"   },
    { name: "salesforce.create_lead",risk: "low",      runs: "2,011" },
  ];
  const riskColor = (r: string) =>
    r === "high"   ? "bg-rose-500/20 text-rose-200 border-rose-400/30" :
    r === "medium" ? "bg-amber-500/20 text-amber-200 border-amber-400/30" :
                     "bg-emerald-500/20 text-emerald-200 border-emerald-400/30";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-white">IT Support Bot · Tool registry</h3>
        <Badge className="bg-white/10 border-white/20 text-white">5 active</Badge>
      </div>
      <div className="divide-y divide-white/5">
        {tools.map(t => (
          <div key={t.name} className="py-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              <Wrench className="w-4 h-4 text-cyan-300" />
              <code className="text-slate-100 font-mono text-[13px]">{t.name}</code>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 tabular-nums">{t.runs} runs</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${riskColor(t.risk)}`}>{t.risk}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg bg-amber-500/10 border border-amber-400/20 p-3 text-xs text-amber-200 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span><strong>2 approvals pending</strong> — Stripe refund of $129 for asha@acme.com awaits sign-off.</span>
      </div>
    </div>
  );
}

/* ============================================================
   LIVE OPS — chat, voice, tickets
   ============================================================ */
function LiveOps() {
  const cards = [
    { icon: Headphones, title: "Live chat",     body: "Operators see queued sessions in real-time. Claim, reply, transfer, resolve.",
      bullets: ["FIFO routing", "Full pre-loaded transcript", "Canned responses", "Operator presence"] },
    { icon: Phone,      title: "Voice & calls", body: "Twilio-powered call centre: inbound queues, voicemail, callbacks, recordings.",
      bullets: ["Live agent dashboard", "Supervisor monitor", "Auto-transcription", "Reports & KPIs"] },
    { icon: Ticket,     title: "Tickets",       body: "Bot-created or human-opened tickets with SLAs, comments, status flow.",
      bullets: ["Zendesk/Jira sync", "SLA tracking", "Threaded comments", "Priority routing"] },
  ];
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead
          eyebrow="Live operations"
          title="Hand off to humans the moment it matters"
          sub="When AI hits the edge, your team picks up — with full context, zero hand-off friction."
        />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {cards.map(c => (
            <div key={c.title} className="rounded-xl border border-sky-200/70 bg-white/75 backdrop-blur-sm p-6 shadow-sm shadow-sky-900/5 hover:shadow-md hover:shadow-sky-900/10 transition">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{c.body}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                {c.bullets.map(b => (
                  <li key={b} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   KNOWLEDGE + AI providers
   ============================================================ */
function KnowledgeAndAI() {
  return (
    <section className="py-20 lg:py-28 bg-sky-200/60">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
        <div className="rounded-2xl border border-cyan-200/70 bg-gradient-to-br from-white/85 to-cyan-50/70 backdrop-blur-sm p-8 shadow-sm shadow-cyan-900/5">
          <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center">
            <Database className="w-5 h-5" />
          </div>
          <h3 className="mt-5 text-2xl font-semibold tracking-tight">Grounded on your knowledge</h3>
          <p className="mt-3 text-slate-600">
            Drop in PDFs, URLs, DOCX, Confluence spaces, or SharePoint sites. We chunk, embed, and serve
            with hybrid retrieval — every answer cites its sources.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["PDF / DOCX", "Web pages", "Confluence", "SharePoint", "Notion", "Google Drive"].map(s => (
              <div key={s} className="rounded-lg border border-sky-200/70 px-3 py-2 text-sm flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-slate-400" /> {s}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-cyan-200/70 bg-gradient-to-br from-white/85 to-cyan-50/70 backdrop-blur-sm p-8 shadow-sm shadow-cyan-900/5">
          <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 grid place-items-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <h3 className="mt-5 text-2xl font-semibold tracking-tight">Bring your own model</h3>
          <p className="mt-3 text-slate-600">
            Plug in OpenAI, Anthropic, Google, Azure OpenAI, AWS Bedrock or any OpenAI-compatible endpoint.
            Routing rules per bot, per channel, per persona.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["OpenAI", "Anthropic", "Google Gemini", "Azure OpenAI", "AWS Bedrock", "Mistral", "Groq", "Self-hosted"].map(s => (
              <span key={s} className="px-3 py-1 rounded-full text-xs border border-sky-200/70 bg-slate-50 text-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   ENTERPRISE READY
   ============================================================ */
function Enterprise() {
  const items = [
    { icon: ShieldCheck, title: "SAML 2.0 & OIDC SSO",  body: "Per-org IdP config, optional SSO enforcement, JIT user provisioning." },
    { icon: Users,       title: "SCIM 2.0 provisioning", body: "Standards-compliant user lifecycle from Okta, Azure AD, Google Workspace." },
    { icon: Lock,        title: "Role-based access",     body: "37 dynamic permissions, custom roles, per-tool ACLs, hierarchical groups." },
    { icon: Eye,         title: "Full audit trail",      body: "Every entity insert/update/delete with JSON diff and actor — exportable." },
    { icon: Building2,   title: "Multi-tenant isolation",body: "Per-tenant data fences, schema-validated tenant filters, IDOR-tested." },
    { icon: Palette,     title: "White-label branding",  body: "Per-tenant logo, palette, typography, custom domain, hidden 'powered by'." },
  ];
  return (
    <section id="enterprise" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead
          eyebrow="Enterprise-ready"
          title="Built for the security & compliance bar of every CISO"
          sub="From day one — not bolted on for the Enterprise tier."
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(i => (
            <div key={i.title} className="rounded-xl border border-sky-200/70 bg-white/75 backdrop-blur-sm p-6 shadow-sm shadow-sky-900/5 hover:shadow-md hover:shadow-sky-900/10 transition">
              <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-700 grid place-items-center">
                <i.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold">{i.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{i.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   ANALYTICS
   ============================================================ */
function Analytics() {
  const stats = [
    { label: "Conversations / week", value: "12,486", delta: "+18%" },
    { label: "Resolution rate",      value: "82.4%",  delta: "+3.1%" },
    { label: "Avg handle time",      value: "1m 42s", delta: "-22s"  },
    { label: "Tool success rate",    value: "98.1%",  delta: "+0.4%" },
  ];
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-cyan-100/50 to-sky-200/70">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <SectionHead
            align="left"
            eyebrow="Analytics"
            title="Know what your AI workforce is doing — to the second"
            sub="Volume, deflection, AI cost, tool-success, top intents, hand-off rate — sliced by bot, channel and time."
          />
          <ul className="mt-6 space-y-3 text-sm text-slate-600">
            <Bullet>Real-time dashboard with 12+ KPIs out of the box.</Bullet>
            <Bullet>Per-tool latency & failure analytics.</Bullet>
            <Bullet>Cost tracking per LLM provider, per bot.</Bullet>
            <Bullet>CSV export for every grid.</Bullet>
          </ul>
        </div>
        <div className="rounded-2xl border border-cyan-200/70 bg-white/80 backdrop-blur-sm p-6 shadow-md shadow-cyan-900/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map(s => (
              <div key={s.label} className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                <div className="text-xs text-slate-500">{s.label}</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{s.value}</div>
                <div className="mt-1 text-xs text-emerald-600 font-medium">{s.delta} vs last week</div>
              </div>
            ))}
          </div>
          <div className="mt-5 h-40 rounded-xl bg-gradient-to-tr from-indigo-50 via-cyan-50 to-emerald-50 border border-slate-100 grid place-items-center text-xs text-slate-400">
            <div className="flex items-end gap-1 px-4 w-full h-full pb-2">
              {[35,55,40,70,60,85,72,90,78,95,82,100].map((h,i) => (
                <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-indigo-400 to-cyan-400" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   USE CASES
   ============================================================ */
function UseCases() {
  const cases = [
    { icon: GraduationCap, title: "Higher Ed", body: "Admissions, financial-aid, course-help bots — multi-campus, multi-language." },
    { icon: Briefcase,     title: "HR & IT",   body: "Internal help-desk: PTO requests, VPN issues, onboarding workflows." },
    { icon: LifeBuoy,      title: "Customer support", body: "Tier-1 deflection, refunds, order tracking — with human handoff." },
    { icon: Building2,     title: "Enterprise IT", body: "ITSM front-end for ServiceNow / Jira / Zendesk with agentic actions." },
  ];
  return (
    <section id="usecases" className="py-20 lg:py-28 bg-sky-200/60">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHead
          eyebrow="Use cases"
          title="Built for the way real teams work"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cases.map(c => (
            <div key={c.title} className="rounded-xl border border-sky-200/70 bg-white/75 backdrop-blur-sm p-6 shadow-sm shadow-sky-900/5 hover:shadow-md hover:shadow-sky-900/10 transition">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-cyan-200/70 bg-gradient-to-br from-white/85 to-cyan-50/70 backdrop-blur-sm p-8 shadow-sm shadow-cyan-900/5 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="flex items-start gap-4">
            <div className="flex -space-x-1">
              {[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
            </div>
            <div>
              <p className="text-slate-800 italic">"We replaced 4 different chatbots and a $60k/yr live-chat tool with CampusAssist. Resolution rate went from 41% to 82% in 3 weeks."</p>
              <p className="mt-2 text-sm text-slate-500">— Director of Student Services, Mid-Atlantic State University</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   FINAL CTA
   ============================================================ */
function FinalCta() {
  const openAuth = useAuthDialog();
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-5xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-cyan-500 px-8 py-14 sm:px-14 sm:py-20 text-white text-center shadow-xl shadow-indigo-900/20">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,_rgba(255,255,255,0.35),_transparent_45%)]" />
          <div className="relative">
            <Badge className="bg-white/15 border-white/20 text-white hover:bg-white/15">
              <Zap className="w-3.5 h-3.5 mr-1.5" /> Live in under 15 minutes
            </Badge>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Deploy your first agent today.
            </h2>
            <p className="mt-4 text-indigo-100 max-w-xl mx-auto">
              Spin up a bot, ground it on your docs, plug it into your stack — live in under 15 minutes.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50" onClick={() => openAuth("signup")} data-testid="button-final-cta">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/10 hover:bg-white/20 hover:text-white" onClick={() => openAuth("signin")} data-testid="button-final-login">
                Sign in
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   FOOTER
   ============================================================ */
function Footer() {
  return (
    <footer className="border-t border-sky-300/60 bg-sky-200/80">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-10 text-sm">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-white">
              <Bot className="w-4 h-4" />
            </div>
            <span className="font-semibold">CampusAssist</span>
          </div>
          <p className="mt-3 text-slate-500">The agentic AI workforce for modern organisations.</p>
        </div>
        <FooterCol title="Product" items={["Capabilities", "Channels", "Use cases"]} />
        <FooterCol title="Enterprise" items={["Security", "SSO & SCIM", "Audit", "White-label"]} />
        <FooterCol title="Company" items={["Docs", "Status", "Contact", "Terms · Privacy"]} />
      </div>
      <div className="border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} CampusAssist. All rights reserved.</span>
          <span className="flex items-center gap-4">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Security</span>
          </span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4 className="font-semibold text-slate-800">{title}</h4>
      <ul className="mt-3 space-y-2 text-slate-500">
        {items.map(i => <li key={i}><span className="text-slate-500">{i}</span></li>)}
      </ul>
    </div>
  );
}

/* ============================================================
   SHARED PRIMITIVES
   ============================================================ */
function SectionHead({ eyebrow, title, sub, align = "center" }: { eyebrow: string; title: string; sub?: string; align?: "center" | "left" }) {
  return (
    <div className={align === "left" ? "max-w-2xl" : "max-w-3xl mx-auto text-center"}>
      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-indigo-600 ${align === "center" ? "justify-center" : ""}`}>
        <Layers className="w-3.5 h-3.5" /> {eyebrow}
      </div>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold tracking-tight leading-tight text-slate-900">
        {title}
      </h2>
      {sub && <p className="mt-4 text-lg text-slate-600">{sub}</p>}
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
